import { cn } from '@/shared/helpers/classnames'

import type { FC } from 'react'

import type { TypographyProps } from './types'

/**
 * Typography component to render text with consistent styling from the design system
 *
 * @example
 * <Typography type="heading" variant="h1-bold">
 *   Main Title
 * </Typography>
 *
 * @example
 * <Typography type="paragraph" variant="md" color="primary">
 *   Body text content
 * </Typography>
 *
 * @example
 * <Typography type="caption">
 *   Caption text
 * </Typography>
 */
export const Typography: FC<TypographyProps> = props => {
  const {
    type,
    as: Component = 'span',
    children,
    color = 'base-content',
    uppercase = false,
    truncate,
    propagate = true,
    propagateColor = true,
  } = props

  const className = cn('typography', {
    uppercase: uppercase,
    // Heading variants
    'typography-h1-bold': type === 'heading' && props.variant === 'h1-bold',
    'typography-h1-medium': type === 'heading' && props.variant === 'h1-medium',
    'typography-h1-regular': type === 'heading' && props.variant === 'h1-regular',
    'typography-h2-bold': type === 'heading' && props.variant === 'h2-bold',
    'typography-h2-medium': type === 'heading' && props.variant === 'h2-medium',
    'typography-h2-regular': type === 'heading' && props.variant === 'h2-regular',
    'typography-h3-bold': type === 'heading' && props.variant === 'h3-bold',
    'typography-h3-medium': type === 'heading' && props.variant === 'h3-medium',
    'typography-h3-regular': type === 'heading' && props.variant === 'h3-regular',
    'typography-h4-bold': type === 'heading' && props.variant === 'h4-bold',
    'typography-h4-medium': type === 'heading' && props.variant === 'h4-medium',
    'typography-h4-regular': type === 'heading' && props.variant === 'h4-regular',
    'typography-h5-bold': type === 'heading' && props.variant === 'h5-bold',
    'typography-h5-medium': type === 'heading' && props.variant === 'h5-medium',
    'typography-h5-regular': type === 'heading' && props.variant === 'h5-regular',

    // Paragraph variants
    'typography-paragraph-lg': type === 'paragraph' && props.variant === 'lg',
    'typography-paragraph-lg-medium': type === 'paragraph' && props.variant === 'lg-medium',
    'typography-paragraph-md': type === 'paragraph' && (props.variant === 'md' || !props.variant),
    'typography-paragraph-md-medium': type === 'paragraph' && props.variant === 'md-medium',
    'typography-paragraph-sm': type === 'paragraph' && props.variant === 'sm',
    'typography-paragraph-sm-medium': type === 'paragraph' && props.variant === 'sm-medium',
    'typography-paragraph-xs': type === 'paragraph' && props.variant === 'xs',
    'typography-paragraph-xs-medium': type === 'paragraph' && props.variant === 'xs-medium',

    // Caption
    'typography-caption': type === 'caption',

    // Label variants
    'typography-label-xs': type === 'label' && props.variant === 'xs',
    'typography-label-sm': type === 'label' && props.variant === 'sm',
    'typography-label-md': type === 'label' && (props.variant === 'md' || !props.variant),

    // Color variants
    'typography-color-base-100': color === 'base-100',
    'typography-color-base-200': color === 'base-200',
    'typography-color-base-300': color === 'base-300',
    'typography-color-base-content': color === 'base-content',
    'typography-color-base-content-50': color === 'base-content-50',
    'typography-color-base-content-30': color === 'base-content-30',
    'typography-color-base-content-20': color === 'base-content-20',
    'typography-color-base-content-15': color === 'base-content-15',
    'typography-color-accent': color === 'accent',
    'typography-color-accent-content': color === 'accent-content',
    'typography-color-neutral': color === 'neutral',
    'typography-color-neutral-content': color === 'neutral-content',
    'typography-color-neutral-500': color === 'neutral-500',
    'typography-color-neutral-700': color === 'neutral-700',
    'typography-color-secondary': color === 'secondary',
    'typography-color-secondary-content': color === 'secondary-content',
    'typography-color-primary': color === 'primary',
    'typography-color-primary-content': color === 'primary-content',
    truncate: truncate,
    'typography-propagate': propagate,
    'typography-propagate-color': propagate && propagateColor,
  })

  return <Component className={className}>{children}</Component>
}
